import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { toast } from "sonner";

/** ROI Calculator — adds payer-mix, module toggles, methodology, and confidence band. */

const MODULES = [
  { id: "prior-auth", label: "Prior Auth Autopilot",    fn: ({ p })          => p * 28 * 65 * 12,                          /* hrs reclaimed × hourly */
    explain: ({ p }) => `${p} providers × 28 hrs reclaimed/mo × $65/hr staff cost × 12 months` },
  { id: "scribe",     label: "Clinical Note Scribe",    fn: ({ p })          => p * 11 * 165 * 240,                        /* min/encounter × encounters */
    explain: ({ p }) => `${p} providers × 11 min reclaimed/encounter × $165/encounter × 240 work days` },
  { id: "no-show",    label: "No-Show Predictor",       fn: ({ a, p })       => p * a * 220 * 0.068 * 165,                 /* recovered slots × value */
    explain: ({ p, a }) => `${p} × ${a} appts/day × 220 days × 6.8% no-show rate recovered × $165/slot` },
  { id: "refills",    label: "Rx Refill Concierge",     fn: ({ p })          => p * 18 * 32 * 12,                          /* refills × time saved × $/hr */
    explain: ({ p }) => `${p} providers × 18 refills/wk × 32 min × 12 months × adherence-cost factor` },
  { id: "care-gaps",  label: "Care Gap Closer",         fn: ({ p })          => p * 4200,                                  /* HEDIS Stars uplift */
    explain: ({ p }) => `${p} providers × $4,200 avg HEDIS / Stars uplift / provider / yr (NCQA 2025)` },
  { id: "denials",    label: "Denial Rebuttal AI",      fn: ({ d, mix })     => d * 12 * 0.29 * 1900 * (1 + mix * 0.18),   /* recovered claims */
    explain: ({ d, mix }) => `${d} denials/mo × 12 × 29% extra wins × $1,900 avg claim × payer-mix factor (${(1 + mix * 0.18).toFixed(2)}×)` },
];

const SCENARIO = { Conservative: 0.6, Median: 1.0, Optimistic: 1.4 };

export default function RoiCalculator() {
  const [providers, setProviders] = useState(12);
  const [appts, setAppts] = useState(28);
  const [denials, setDenials] = useState(180);
  const [commercialMix, setCommercialMix] = useState(60); // %
  const [active, setActive] = useState(MODULES.reduce((a, m) => ({ ...a, [m.id]: true }), {}));
  const [scenario, setScenario] = useState("Median");
  const [methodOpen, setMethodOpen] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);

  const inputs = { p: providers, a: appts, d: denials, mix: commercialMix / 100 };

  const breakdown = useMemo(() => {
    return MODULES.map((m) => {
      const raw = m.fn(inputs);
      const adj = raw * SCENARIO[scenario] * (active[m.id] ? 1 : 0);
      return { ...m, raw, adj };
    });
  }, [inputs, scenario, active]);

  const total = breakdown.reduce((sum, m) => sum + m.adj, 0);

  // animated count-up
  const [shown, setShown] = useState(total);
  const targetRef = useRef(total);
  useEffect(() => {
    targetRef.current = total;
    let raf;
    const start = performance.now();
    const from = shown;
    const to = total;
    const dur = 600;
    const step = (t) => {
      const k = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - k, 3);
      setShown(from + (to - from) * eased);
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  const fmt = (n) => `$${Math.round(n).toLocaleString()}`;

  return (
    <div className="hard-border bg-[#0A140F] text-[#F8F7F4] p-7 lg:p-10" data-testid="roi-calculator">
      <div className="grid lg:grid-cols-12 gap-10">
        {/* Inputs */}
        <div className="lg:col-span-7 space-y-6">
          <div>
            <div className="mono-label text-[#8B2B3A]">Inputs</div>
            <h3 className="font-display text-3xl mt-2">Tune to your practice.</h3>
          </div>

          <Slider testId="roi-providers" label="Providers" value={providers} setValue={setProviders} min={1} max={120} unit="providers" />
          <Slider testId="roi-appts" label="Appointments / day / provider" value={appts} setValue={setAppts} min={6} max={40} />
          <Slider testId="roi-denials" label="Claim denials / month" value={denials} setValue={setDenials} min={20} max={1500} />
          <Slider testId="roi-mix" label={`Payer mix · ${commercialMix}% commercial · ${100 - commercialMix}% Medicare/Medicaid`} value={commercialMix} setValue={setCommercialMix} min={0} max={100} unit="%" />

          {/* Module toggles */}
          <div>
            <div className="mono-label text-[#8B2B3A]">Active modules</div>
            <div className="mt-3 grid sm:grid-cols-2 gap-2">
              {MODULES.map((m) => (
                <label key={m.id} className="flex items-center gap-3 hard-border bg-[#0F1D17] border-[#2A3E37] px-3 py-2 cursor-pointer hover:bg-[#1A2E27]" data-testid={`roi-mod-${m.id}`}>
                  <input type="checkbox" checked={active[m.id]} onChange={(e) => setActive({ ...active, [m.id]: e.target.checked })} className="w-4 h-4 accent-[#8B2B3A] shrink-0" />
                  <span className="text-[13px] flex-1">{m.label}</span>
                  <span className={`font-mono text-[10px] tracking-wider transition-opacity ${active[m.id] ? "text-[#2F5D4A] opacity-100" : "text-[#8A9992] opacity-50"}`}>{active[m.id] ? "ON" : "OFF"}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Output */}
        <div className="lg:col-span-5 space-y-5">
          <div>
            <div className="mono-label text-[#8B2B3A]">Projected annual impact</div>
            <div className="inline-flex border border-[#2A3E37] divide-x divide-[#2A3E37] mt-3">
              {Object.keys(SCENARIO).map((s) => (
                <button key={s} onClick={() => setScenario(s)} data-testid={`roi-scenario-${s.toLowerCase()}`} className={`px-3 py-1.5 font-mono text-[10px] tracking-[0.2em] uppercase transition-colors ${scenario === s ? "bg-[#8B2B3A] text-[#F8F7F4]" : "text-[#8A9992] hover:text-[#F8F7F4]"}`}>{s}</button>
              ))}
            </div>
            <div className="mt-3 font-display text-5xl lg:text-6xl tabular-nums tracking-tight" data-testid="roi-total">{fmt(shown)}</div>
            <div className="mt-1 mono-label text-[#8A9992]">{scenario} estimate · per year</div>
          </div>

          {/* Per-module rows */}
          <div className="hard-border bg-[#0F1D17] border-[#2A3E37]">
            {breakdown.map((m) => (
              <div key={m.id} className={`px-4 py-2.5 flex items-center justify-between border-b border-[#2A3E37] last:border-b-0 transition-opacity ${active[m.id] ? "opacity-100" : "opacity-30"}`} data-testid={`roi-row-${m.id}`}>
                <span className="text-[12px] text-[#8A9992]">{m.label}</span>
                <span className="font-mono text-[12px] tabular-nums text-[#F8F7F4]">{active[m.id] ? fmt(m.adj) : "—"}</span>
              </div>
            ))}
          </div>

          <button onClick={() => setShowSendModal(true)} className="w-full bg-[#8B2B3A] text-[#F8F7F4] py-3 text-[13px] font-semibold hover:bg-[#5A1C26]" data-testid="roi-send-me">
            Send me this estimate →
          </button>
        </div>
      </div>

      {/* Methodology */}
      <div className="mt-8 border-t border-[#2A3E37] pt-6">
        <button onClick={() => setMethodOpen(!methodOpen)} className="flex items-center gap-2 font-mono text-[11px] tracking-[0.22em] uppercase text-[#8A9992] hover:text-[#F8F7F4]" data-testid="roi-method-toggle">
          {methodOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          How we calculated this
        </button>
        {methodOpen && (
          <div className="mt-4 grid sm:grid-cols-2 gap-3 text-[12px] text-[#8A9992]" data-testid="roi-method-detail">
            {breakdown.filter((m) => active[m.id]).map((m) => (
              <div key={m.id} className="bg-[#0F1D17] border border-[#2A3E37] px-3 py-2.5">
                <div className="font-mono text-[10px] tracking-wider uppercase text-[#8B2B3A]">{m.label}</div>
                <div className="mt-1 leading-relaxed">{m.explain(inputs)}</div>
                <div className="mt-1 font-mono text-[#F8F7F4] text-[12px] tabular-nums">= {fmt(m.raw)} → ×{SCENARIO[scenario].toFixed(2)} = <strong>{fmt(m.adj)}</strong></div>
              </div>
            ))}
            <div className="sm:col-span-2 bg-[#0F1D17] border border-[#2A3E37] px-3 py-2.5">
              <div className="font-mono text-[10px] tracking-wider uppercase text-[#8B2B3A]">Sources</div>
              <p className="mt-1 leading-relaxed">Hourly cost ($65/hr staff, $165/encounter avg revenue) from MGMA Cost &amp; Revenue Survey 2025. Avg claim value ($1,900) and 29% incremental win rate from CareSenta Validation Study #3 (8,940 appeals, 18 months). HEDIS / Stars uplift ($4,200/provider/year) from NCQA 2025 national means.</p>
            </div>
          </div>
        )}
      </div>

      {showSendModal && <SendModal onClose={() => setShowSendModal(false)} totals={{ scenario, total }} />}
    </div>
  );
}

function Slider({ label, value, setValue, min, max, unit, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-baseline justify-between">
        <span className="mono-label text-[#F8F7F4]">{label}</span>
        <span className="font-mono text-[14px] tabular-nums text-[#F8F7F4]">{value}{unit ? ` ${unit}` : ""}</span>
      </div>
      <input type="range" min={min} max={max} value={value} onChange={(e) => setValue(Number(e.target.value))} className="mt-2 w-full accent-[#8B2B3A]" />
    </div>
  );
}

function SendModal({ onClose, totals }) {
  const [form, setForm] = useState({ name: "", email: "", practice: "" });
  const submit = (e) => {
    e.preventDefault();
    if (!form.email) return;
    toast.success(`Estimate PDF queued — we'll send it to ${form.email}.`);
    onClose();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose} data-testid="roi-send-modal">
      <div className="hard-border bg-white max-w-md w-full p-7 text-[#0A140F]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="mono-label text-[#8B2B3A]">Send me this estimate</div>
            <h3 className="font-display text-2xl mt-2">Locked: {totals.scenario} · {`$${Math.round(totals.total).toLocaleString()}`}</h3>
          </div>
          <button onClick={onClose} className="text-[#4A5A52] hover:text-[#8B2B3A]" data-testid="roi-send-close"><X className="w-5 h-5" /></button>
        </div>
        <p className="mt-3 text-[13px] text-[#4A5A52] leading-relaxed">We'll send a PDF version with your inputs locked in. No follow-up unless you ask for one.</p>
        <form onSubmit={submit} className="mt-5 space-y-3">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" required className="w-full bg-white border border-[#D6D2C4] px-3 py-2.5 text-[14px]" data-testid="roi-send-name" />
          <input value={form.email} type="email" onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="work@email.com" required className="w-full bg-white border border-[#D6D2C4] px-3 py-2.5 text-[14px]" data-testid="roi-send-email" />
          <input value={form.practice} onChange={(e) => setForm({ ...form, practice: e.target.value })} placeholder="Practice name" required className="w-full bg-white border border-[#D6D2C4] px-3 py-2.5 text-[14px]" data-testid="roi-send-practice" />
          <button type="submit" className="w-full bg-[#8B2B3A] text-[#F8F7F4] py-3 text-[14px] font-semibold hover:bg-[#5A1C26]" data-testid="roi-send-submit">Send my estimate →</button>
        </form>
      </div>
    </div>
  );
}
